import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  User, Mail, Phone, MapPin, Calendar, ArrowLeft, Plus, Edit, Trash2,
  FileText, Briefcase, Receipt, CreditCard, Package, MessageSquare,
  Upload, Send, DollarSign, TrendingUp, Clock, Home, Building,
  Star, Eye, ChevronDown, ChevronRight, Loader2, X, Check,
  FileCheck, Download, Activity, Printer
} from 'lucide-react';
import JobDetails from './JobDetails';
import AdvancedQuoteCreator from './AdvancedQuoteCreator';
import QuoteView from './QuoteView';
import InvoiceView from './InvoiceView';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function CustomerProfile({ customerId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [services, setServices] = useState([]);
  const [handymen, setHandymen] = useState([]);
  
  // Dialog states
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showEditProperty, setShowEditProperty] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  
  // Module Dialog states
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showQuoteCreator, setShowQuoteCreator] = useState(false);
  const [showQuoteView, setShowQuoteView] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [jobLead, setJobLead] = useState(null);
  
  // Form states
  const [editingProperty, setEditingProperty] = useState(null);
  const [propertyForm, setPropertyForm] = useState({
    property_name: 'Home',
    address_line_1: '',
    city: '',
    state: '',
    pincode: '',
    latitude: '',
    longitude: '',
    is_primary: false
  });
  const [noteForm, setNoteForm] = useState('');
  const [documentForm, setDocumentForm] = useState({ filename: '', file_url: '' });
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [jobForm, setJobForm] = useState({
    service_id: '',
    handyman_id: '',
    scheduled_date: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    if (customerId) {
      fetchCustomerProfile();
      fetchServices();
      fetchHandymen();
    }
  }, [customerId]);

  const fetchCustomerProfile = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/customers/${customerId}/profile`);
      setProfileData(response.data);
    } catch (error) {
      toast.error('Failed to load customer profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axiosInstance.get('/services');
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services');
    }
  };

  const fetchHandymen = async () => {
    try {
      const response = await axiosInstance.get('/handymen');
      setHandymen(response.data);
    } catch (error) {
      console.error('Failed to fetch handymen');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'America/Phoenix',
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      qualified: 'bg-purple-100 text-purple-700',
      converted: 'bg-green-100 text-green-700',
      converted_to_job: 'bg-green-100 text-green-700',
      lost: 'bg-red-100 text-red-700',
      pending: 'bg-yellow-100 text-yellow-700',
      sent: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-gray-200 text-[#595959]',
      assigned: 'bg-purple-100 text-purple-700',
      in_progress: 'bg-orange-100 text-orange-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      active: 'bg-green-100 text-green-700',
      expired: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Property handlers
  const handleAddProperty = async () => {
    if (!propertyForm.address_line_1) {
      toast.error('Address Line 1 is required');
      return;
    }
    try {
      // Build full address from components
      const addressParts = [propertyForm.address_line_1];
      if (propertyForm.city) addressParts.push(propertyForm.city);
      if (propertyForm.state) addressParts.push(propertyForm.state);
      if (propertyForm.pincode) addressParts.push(propertyForm.pincode);
      const full_address = addressParts.join(', ');
      
      await axiosInstance.post(`/customers/${customerId}/properties`, {
        property_name: propertyForm.property_name,
        address_line_1: propertyForm.address_line_1,
        full_address: full_address,
        city: propertyForm.city,
        state: propertyForm.state,
        pincode: propertyForm.pincode,
        latitude: propertyForm.latitude ? parseFloat(propertyForm.latitude) : null,
        longitude: propertyForm.longitude ? parseFloat(propertyForm.longitude) : null,
        is_primary: propertyForm.is_primary
      });
      toast.success('Property added successfully');
      setShowAddProperty(false);
      setPropertyForm({ property_name: 'Home', address_line_1: '', city: '', state: '', pincode: '', latitude: '', longitude: '', is_primary: false });
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to add property');
    }
  };

  const handleEditProperty = async () => {
    if (!editingProperty) return;
    if (!propertyForm.address_line_1) {
      toast.error('Address Line 1 is required');
      return;
    }
    try {
      // Build full address from components
      const addressParts = [propertyForm.address_line_1];
      if (propertyForm.city) addressParts.push(propertyForm.city);
      if (propertyForm.state) addressParts.push(propertyForm.state);
      if (propertyForm.pincode) addressParts.push(propertyForm.pincode);
      const full_address = addressParts.join(', ');
      
      await axiosInstance.put(`/customers/${customerId}/properties/${editingProperty.id}`, {
        property_name: propertyForm.property_name,
        full_address: full_address,
        city: propertyForm.city,
        state: propertyForm.state,
        pincode: propertyForm.pincode,
        latitude: propertyForm.latitude ? parseFloat(propertyForm.latitude) : null,
        longitude: propertyForm.longitude ? parseFloat(propertyForm.longitude) : null,
        is_primary: propertyForm.is_primary
      });
      toast.success('Property updated successfully');
      setShowEditProperty(false);
      setEditingProperty(null);
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to update property');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!confirm('Are you sure you want to delete this property?')) return;
    try {
      await axiosInstance.delete(`/customers/${customerId}/properties/${propertyId}`);
      toast.success('Property deleted');
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleSetPrimary = async (propertyId) => {
    try {
      await axiosInstance.put(`/customers/${customerId}/properties/${propertyId}`, { is_primary: true });
      toast.success('Primary property updated');
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to update primary property');
    }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!noteForm.trim()) return;
    try {
      await axiosInstance.post(`/customers/${customerId}/notes`, { note: noteForm, author: 'Admin' });
      toast.success('Note added');
      setShowAddNote(false);
      setNoteForm('');
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axiosInstance.delete(`/customers/${customerId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  // Document handlers
  const handleAddDocument = async () => {
    if (!documentForm.filename || !documentForm.file_url) return;
    try {
      await axiosInstance.post(`/customers/${customerId}/documents`, documentForm);
      toast.success('Document added');
      setShowAddDocument(false);
      setDocumentForm({ filename: '', file_url: '' });
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to add document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axiosInstance.delete(`/customers/${customerId}/documents/${docId}`);
      toast.success('Document deleted');
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Email handler
  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.message) {
      toast.error('Please fill in subject and message');
      return;
    }
    try {
      await axiosInstance.post(`/customers/${customerId}/send-email`, emailForm);
      toast.success('Email sent successfully');
      setShowSendEmail(false);
      setEmailForm({ subject: '', message: '' });
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  // Job creation handler
  const handleCreateJob = async () => {
    if (!jobForm.service_id || !jobForm.scheduled_date) {
      toast.error('Please select a service and scheduled date');
      return;
    }
    try {
      await axiosInstance.post('/jobs', {
        customer_id: customerId,
        service_id: jobForm.service_id,
        handyman_id: jobForm.handyman_id || null,
        scheduled_date: new Date(jobForm.scheduled_date).toISOString(),
        address: jobForm.address || profileData?.primary_property?.full_address || profileData?.customer?.address || '',
        notes: jobForm.notes
      });
      toast.success('Job created successfully');
      setShowCreateJob(false);
      setJobForm({ service_id: '', handyman_id: '', scheduled_date: '', address: '', notes: '' });
      fetchCustomerProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  const openEditProperty = (property) => {
    setEditingProperty(property);
    // Try to parse address components from full_address if individual fields not available
    const addressLine = property.address_line_1 || property.full_address?.split(',')[0]?.trim() || '';
    setPropertyForm({
      property_name: property.property_name || 'Home',
      address_line_1: addressLine,
      city: property.city || '',
      state: property.state || '',
      pincode: property.pincode || '',
      latitude: property.latitude || '',
      longitude: property.longitude || '',
      is_primary: property.is_primary || false
    });
    setShowEditProperty(true);
  };

  // Navigation handlers
  const openJobDetails = (jobId) => {
    setSelectedJobId(jobId);
    setShowJobDetails(true);
  };

  const openQuoteCreator = (quote = null) => {
    if (quote) {
      // Existing quote - open in view mode
      setSelectedQuote(quote);
      setShowQuoteView(true);
    } else {
      // New quote - open creator
      setSelectedQuote(null);
      setShowQuoteCreator(true);
    }
  };

  const openLeadDetails = (lead) => {
    setSelectedLead(lead);
    setShowLeadDetails(true);
  };

  const openInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetails(true);
  };

  const openCreateJob = () => {
    setJobLead(createMockLeadForQuote());
    setShowCreateJob(true);
  };

  // Create a mock lead object for the quote creator (when creating new quote)
  const createMockLeadForQuote = () => {
    const customer = profileData?.customer;
    const primaryProperty = profileData?.primary_property;
    return {
      id: `temp-${Date.now()}`,
      customer_id: customerId,
      lead_data: {
        full_name: customer?.name,
        name: customer?.name,
        email: customer?.email,
        phone: customer?.phone,
        address: primaryProperty?.full_address || customer?.address || '',
        service_needed: ''
      }
    };
  };

  // Send invoice to customer
  const handleSendInvoice = async (invoiceId) => {
    try {
      await axiosInstance.post(`/invoices/${invoiceId}/send`);
      toast.success('Invoice sent to customer');
      fetchCustomerProfile();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#595959]" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Customer not found</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Customers
        </Button>
      </div>
    );
  }

  const { customer, properties, statistics, leads, quotes, jobs, packages, invoices, payments, notes, documents } = profileData;

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack} className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Customer Profile
          </h2>
          <p className="text-gray-500">Complete 360° view of customer</p>
        </div>
      </div>

      {/* Customer Header Card */}
      <Card className="border-l-4 border-l-teal-600">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-[#595959]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{customer.name}</h3>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {customer.email}</span>
                  <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {customer.phone}</span>
                </div>
                <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-400">
                  <span>ID: {customer.id?.slice(0, 8).toUpperCase()}</span>
                  <span>Created: {formatDate(customer.created_at)}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => openQuoteCreator(null)}>
                <FileText className="w-4 h-4 mr-1" /> Create Quote
              </Button>
              <Button variant="outline" size="sm" onClick={openCreateJob}>
                <Briefcase className="w-4 h-4 mr-1" /> Create Job
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddProperty(true)}>
                <Home className="w-4 h-4 mr-1" /> Add Property
              </Button>
              <Button className="bg-[#595959] hover:bg-[#404040]" size="sm" onClick={() => setShowSendEmail(true)}>
                <Send className="w-4 h-4 mr-1" /> Send Email
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-gray-500">Total Jobs</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{statistics.total_jobs}</p>
            <p className="text-xs text-gray-400">{statistics.completed_jobs} completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-green-500" />
              <span className="text-xs text-gray-500">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_revenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Check className="w-4 h-4 text-teal-500" />
              <span className="text-xs text-gray-500">Total Paid</span>
            </div>
            <p className="text-2xl font-bold text-[#595959]">{formatCurrency(statistics.total_paid)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-gray-500">Outstanding</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(statistics.total_outstanding)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500">Quotes</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{statistics.total_quotes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-indigo-500" />
              <span className="text-xs text-gray-500">Active Packages</span>
            </div>
            <p className="text-2xl font-bold text-indigo-600">{statistics.active_packages}</p>
          </CardContent>
        </Card>
      </div>

      {/* Properties Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Home className="w-5 h-5 text-[#595959]" />
              Properties
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setShowAddProperty(true)}>
              <Plus className="w-4 h-4 mr-1" /> Add Property
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {properties.map((prop) => (
                <div key={prop.id} className={`p-4 border rounded-lg ${prop.is_primary ? 'border-teal-500 bg-gray-100' : ''}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">{prop.property_name}</span>
                      {prop.is_primary && <Badge className="bg-gray-200 text-[#595959]">Primary</Badge>}
                    </div>
                    <div className="flex gap-1">
                      {!prop.is_primary && (
                        <Button size="sm" variant="ghost" onClick={() => handleSetPrimary(prop.id)} title="Set as Primary">
                          <Star className="w-4 h-4 text-amber-500" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEditProperty(prop)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteProperty(prop.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{prop.full_address || prop.address_line_1}</p>
                  <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                    {prop.city && <span>{prop.city}</span>}
                    {prop.state && <span>{prop.city ? ', ' : ''}{prop.state}</span>}
                    {prop.pincode && <span>{(prop.city || prop.state) ? ' - ' : ''}{prop.pincode}</span>}
                  </div>
                  {prop.latitude && prop.longitude && (
                    <p className="text-xs text-gray-400 mt-1">
                      <MapPin className="w-3 h-3 inline" /> {prop.latitude}, {prop.longitude}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Home className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No properties added yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leads Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#595959]" />
            Requests / Leads
            <Badge variant="outline">{leads.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">Lead ID</th>
                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    <th className="text-left p-3 font-medium text-gray-600">Service</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-center p-3 font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.slice(0, 10).map((lead) => (
                    <tr 
                      key={lead.id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => openLeadDetails(lead)}
                    >
                      <td className="p-3 font-medium text-[#595959]">#{lead.id?.slice(0, 8).toUpperCase()}</td>
                      <td className="p-3">{formatDate(lead.created_at)}</td>
                      <td className="p-3">{lead.service_type || lead.lead_data?.service_needed || 'General'}</td>
                      <td className="p-3"><Badge className={getStatusColor(lead.status)}>{lead.status?.replace('_', ' ')}</Badge></td>
                      <td className="p-3 text-center">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openLeadDetails(lead); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-6 text-gray-500">No leads found</p>
          )}
        </CardContent>
      </Card>

      {/* Quotes Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#595959]" />
              Quotes
              <Badge variant="outline">{quotes.length}</Badge>
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => openQuoteCreator(null)}>
              <Plus className="w-4 h-4 mr-1" /> New Quote
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {quotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">Quote ID</th>
                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    <th className="text-right p-3 font-medium text-gray-600">Amount</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-center p-3 font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.slice(0, 10).map((quote) => (
                    <tr 
                      key={quote.id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => openQuoteCreator(quote)}
                    >
                      <td className="p-3 font-medium text-[#595959]">{quote.quote_number || `#${quote.id?.slice(0, 8).toUpperCase()}`}</td>
                      <td className="p-3">{formatDate(quote.created_at)}</td>
                      <td className="p-3 text-right font-medium">{formatCurrency(quote.total_amount)}</td>
                      <td className="p-3"><Badge className={getStatusColor(quote.status)}>{quote.status}</Badge></td>
                      <td className="p-3 text-center">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openQuoteCreator(quote); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-6 text-gray-500">No quotes found</p>
          )}
        </CardContent>
      </Card>

      {/* Jobs Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#595959]" />
              Jobs
              <Badge variant="outline">{jobs.length}</Badge>
            </CardTitle>
            <Button size="sm" variant="outline" onClick={openCreateJob}>
              <Plus className="w-4 h-4 mr-1" /> New Job
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {jobs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">Job ID</th>
                    <th className="text-left p-3 font-medium text-gray-600">Service</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-right p-3 font-medium text-gray-600">Amount</th>
                    <th className="text-center p-3 font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.slice(0, 10).map((job) => (
                    <tr 
                      key={job.id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => openJobDetails(job.id)}
                    >
                      <td className="p-3 font-medium text-[#595959]">#{job.id?.slice(0, 8).toUpperCase()}</td>
                      <td className="p-3">{job.service_name || 'General'}</td>
                      <td className="p-3"><Badge className={getStatusColor(job.status)}>{job.status?.replace('_', ' ')}</Badge></td>
                      <td className="p-3 text-right font-medium">{formatCurrency(job.quote_amount)}</td>
                      <td className="p-3 text-center">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openJobDetails(job.id); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-6 text-gray-500">No jobs found</p>
          )}
        </CardContent>
      </Card>

      {/* Packages / AMC Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-[#595959]" />
            Packages / AMC
            <Badge variant="outline">{packages.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {packages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">Package Name</th>
                    <th className="text-left p-3 font-medium text-gray-600">Start</th>
                    <th className="text-left p-3 font-medium text-gray-600">End</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-right p-3 font-medium text-gray-600">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg) => (
                    <tr key={pkg.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{pkg.package_name}</td>
                      <td className="p-3">{formatDate(pkg.purchase_date)}</td>
                      <td className="p-3">{formatDate(pkg.expiry_date)}</td>
                      <td className="p-3"><Badge className={getStatusColor(pkg.is_active ? 'active' : 'expired')}>{pkg.is_active ? 'Active' : 'Expired'}</Badge></td>
                      <td className="p-3 text-right">{pkg.remaining_hours}/{pkg.total_hours}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-6 text-gray-500">No packages found</p>
          )}
        </CardContent>
      </Card>

      {/* Invoices & Payments Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Invoices */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#595959]" />
              Invoices
              <Badge variant="outline">{invoices.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length > 0 ? (
              <div className="space-y-3">
                {invoices.slice(0, 5).map((inv) => (
                  <div 
                    key={inv.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => openInvoiceDetails(inv)}
                  >
                    <div>
                      <p className="font-medium text-[#595959]">{inv.invoice_number}</p>
                      <p className="text-xs text-gray-400">{formatDate(inv.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(inv.total_amount)}</p>
                      <Badge className={getStatusColor(inv.status)}>{inv.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-gray-500">No invoices found</p>
            )}
          </CardContent>
        </Card>

        {/* Payments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#595959]" />
              Payments
              <Badge variant="outline">{payments.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length > 0 ? (
              <div className="space-y-3">
                {payments.slice(0, 5).map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div>
                      <p className="font-medium">{payment.transaction_id || `#${payment.id?.slice(0, 8)}`}</p>
                      <p className="text-xs text-gray-400">{formatDate(payment.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-600">{formatCurrency(payment.amount)}</p>
                      <p className="text-xs text-gray-500 capitalize">{payment.payment_mode}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-gray-500">No payments found</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Documents & Notes Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notes */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#595959]" />
                Internal Notes
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowAddNote(true)}>
                <Plus className="w-4 h-4 mr-1" /> Add Note
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {notes.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {notes.map((note) => (
                  <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{note.author}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{formatDate(note.created_at)}</span>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteNote(note.id)} className="h-6 w-6 p-0 text-red-500">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700">{note.note}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-gray-500">No notes yet</p>
            )}
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-[#595959]" />
                Documents
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowAddDocument(true)}>
                <Upload className="w-4 h-4 mr-1" /> Upload
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {documents.length > 0 ? (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-gray-400" />
                      <div>
                        <p className="font-medium">{doc.filename}</p>
                        <p className="text-xs text-gray-400">Uploaded by {doc.uploaded_by}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => window.open(doc.file_url, '_blank')}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteDocument(doc.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-gray-500">No documents uploaded</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Timeline / Activity Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#595959]" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            <div className="space-y-4">
              {notes.slice(0, 5).map((note, index) => (
                <div key={index} className="relative pl-10">
                  <div className="absolute left-2 w-4 h-4 rounded-full bg-gray-1000 border-2 border-teal-500"></div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">Note Added</span>
                      <span className="text-xs text-gray-500">{formatDate(note.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-600">{note.note}</p>
                    <p className="text-xs text-gray-400 mt-1">By: {note.author}</p>
                  </div>
                </div>
              ))}
              {notes.length === 0 && (
                <p className="text-center py-6 text-gray-500">No recent activity</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ========== DIALOGS ========== */}

      {/* Add Property Dialog */}
      <Dialog open={showAddProperty} onOpenChange={setShowAddProperty}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Property</DialogTitle>
            <DialogDescription>Add a new property for this customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Property Name</Label>
              <Select 
                value={propertyForm.property_name} 
                onValueChange={(value) => setPropertyForm({ ...propertyForm, property_name: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Home">Home</SelectItem>
                  <SelectItem value="Office">Office</SelectItem>
                  <SelectItem value="Villa">Villa</SelectItem>
                  <SelectItem value="Apartment">Apartment</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Address Line 1 <span className="text-red-500">*</span></Label>
              <Input 
                value={propertyForm.address_line_1} 
                onChange={(e) => setPropertyForm({ ...propertyForm, address_line_1: e.target.value })} 
                placeholder="Street address, apartment, suite, etc."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input 
                  value={propertyForm.city} 
                  onChange={(e) => setPropertyForm({ ...propertyForm, city: e.target.value })} 
                  placeholder="City"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input 
                  value={propertyForm.state} 
                  onChange={(e) => setPropertyForm({ ...propertyForm, state: e.target.value })} 
                  placeholder="State"
                />
              </div>
            </div>
            <div>
              <Label>Pincode</Label>
              <Input 
                value={propertyForm.pincode} 
                onChange={(e) => setPropertyForm({ ...propertyForm, pincode: e.target.value })} 
                placeholder="Pincode / Zip code"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lat (optional)</Label>
                <Input 
                  type="number" 
                  step="any" 
                  value={propertyForm.latitude} 
                  onChange={(e) => setPropertyForm({ ...propertyForm, latitude: e.target.value })} 
                  placeholder="Latitude"
                />
              </div>
              <div>
                <Label>Lng (optional)</Label>
                <Input 
                  type="number" 
                  step="any" 
                  value={propertyForm.longitude} 
                  onChange={(e) => setPropertyForm({ ...propertyForm, longitude: e.target.value })} 
                  placeholder="Longitude"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="is_primary" 
                checked={propertyForm.is_primary} 
                onChange={(e) => setPropertyForm({ ...propertyForm, is_primary: e.target.checked })} 
              />
              <Label htmlFor="is_primary">Set as primary property</Label>
            </div>
            <Button onClick={handleAddProperty} className="w-full bg-[#595959] hover:bg-[#404040]">
              Add Property
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Property Dialog */}
      <Dialog open={showEditProperty} onOpenChange={setShowEditProperty}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Property</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Property Name</Label>
              <Select 
                value={propertyForm.property_name} 
                onValueChange={(value) => setPropertyForm({ ...propertyForm, property_name: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Home">Home</SelectItem>
                  <SelectItem value="Office">Office</SelectItem>
                  <SelectItem value="Villa">Villa</SelectItem>
                  <SelectItem value="Apartment">Apartment</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Address Line 1 <span className="text-red-500">*</span></Label>
              <Input 
                value={propertyForm.address_line_1} 
                onChange={(e) => setPropertyForm({ ...propertyForm, address_line_1: e.target.value })} 
                placeholder="Street address, apartment, suite, etc."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input 
                  value={propertyForm.city} 
                  onChange={(e) => setPropertyForm({ ...propertyForm, city: e.target.value })} 
                  placeholder="City"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input 
                  value={propertyForm.state} 
                  onChange={(e) => setPropertyForm({ ...propertyForm, state: e.target.value })} 
                  placeholder="State"
                />
              </div>
            </div>
            <div>
              <Label>Pincode</Label>
              <Input 
                value={propertyForm.pincode} 
                onChange={(e) => setPropertyForm({ ...propertyForm, pincode: e.target.value })} 
                placeholder="Pincode / Zip code"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lat (optional)</Label>
                <Input 
                  type="number" 
                  step="any" 
                  value={propertyForm.latitude} 
                  onChange={(e) => setPropertyForm({ ...propertyForm, latitude: e.target.value })} 
                  placeholder="Latitude"
                />
              </div>
              <div>
                <Label>Lng (optional)</Label>
                <Input 
                  type="number" 
                  step="any" 
                  value={propertyForm.longitude} 
                  onChange={(e) => setPropertyForm({ ...propertyForm, longitude: e.target.value })} 
                  placeholder="Longitude"
                />
              </div>
            </div>
            <Button onClick={handleEditProperty} className="w-full bg-[#595959] hover:bg-[#404040]">
              Update Property
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>Add an internal note for this customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea value={noteForm} onChange={(e) => setNoteForm(e.target.value)} placeholder="Enter your note..." rows={4} />
            <Button onClick={handleAddNote} className="w-full bg-[#595959] hover:bg-[#404040]">Add Note</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={showAddDocument} onOpenChange={setShowAddDocument}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Add a document for this customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Document Name</Label>
              <Input value={documentForm.filename} onChange={(e) => setDocumentForm({ ...documentForm, filename: e.target.value })} placeholder="e.g., Service Agreement" />
            </div>
            <div>
              <Label>Document URL</Label>
              <Input value={documentForm.file_url} onChange={(e) => setDocumentForm({ ...documentForm, file_url: e.target.value })} placeholder="https://..." />
            </div>
            <Button onClick={handleAddDocument} className="w-full bg-[#595959] hover:bg-[#404040]">Upload Document</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showSendEmail} onOpenChange={setShowSendEmail}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email to {customer.name}</DialogTitle>
            <DialogDescription>Email will be sent to {customer.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject</Label>
              <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="Email subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Your message..." rows={6} />
            </div>
            <Button onClick={handleSendEmail} className="w-full bg-[#595959] hover:bg-[#404040]">
              <Send className="w-4 h-4 mr-2" /> Send Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Job - Advanced Form */}
      <AdvancedQuoteCreator
        open={showCreateJob}
        onOpenChange={setShowCreateJob}
        lead={jobLead}
        jobMode={true}
        onSuccess={() => {
          fetchCustomerProfile();
          setShowCreateJob(false);
        }}
      />

      {/* Lead Details Dialog */}
      <Dialog open={showLeadDetails} onOpenChange={setShowLeadDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Lead ID</p>
                  <p className="font-medium">#{selectedLead.id?.slice(0, 8).toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={getStatusColor(selectedLead.status)}>{selectedLead.status?.replace('_', ' ')}</Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p>{formatDate(selectedLead.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Source</p>
                  <p>{selectedLead.source || 'Direct'}</p>
                </div>
              </div>
              <hr />
              <div>
                <p className="text-xs text-gray-500 mb-2">Lead Data</p>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p>{selectedLead.lead_data?.full_name || selectedLead.lead_data?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p>{selectedLead.lead_data?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p>{selectedLead.lead_data?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Service Needed</p>
                    <p>{selectedLead.lead_data?.service_needed || selectedLead.service_type || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Address</p>
                    <p>{selectedLead.lead_data?.address || 'N/A'}</p>
                  </div>
                  {selectedLead.lead_data?.notes && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500">Notes</p>
                      <p>{selectedLead.lead_data.notes}</p>
                    </div>
                  )}
                </div>
              </div>
              {selectedLead.status !== 'converted_to_job' && selectedLead.status !== 'converted' && (
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setShowLeadDetails(false); openQuoteCreator(null); }}>
                    <FileText className="w-4 h-4 mr-2" /> Create Quote
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Professional Invoice View - Unified Template */}
      <InvoiceView
        open={showInvoiceDetails}
        onOpenChange={(open) => {
          setShowInvoiceDetails(open);
          if (!open) {
            setSelectedInvoice(null);
            fetchCustomerProfile();
          }
        }}
        invoiceId={selectedInvoice?.id}
        invoiceData={selectedInvoice}
        onRefresh={fetchCustomerProfile}
        editable={selectedInvoice?.status !== 'paid'}
      />

      {/* Job Details Dialog */}
      <JobDetails 
        jobId={selectedJobId} 
        open={showJobDetails} 
        onClose={() => { setShowJobDetails(false); setSelectedJobId(null); }} 
      />

      {/* Advanced Quote Creator Dialog */}
      <AdvancedQuoteCreator
        open={showQuoteCreator}
        onOpenChange={setShowQuoteCreator}
        lead={selectedQuote ? null : createMockLeadForQuote()}
        existingQuote={selectedQuote}
        onSuccess={() => {
          setShowQuoteCreator(false);
          setSelectedQuote(null);
          fetchCustomerProfile();
        }}
      />

      {/* Quote View Dialog */}
      <QuoteView
        open={showQuoteView}
        onOpenChange={(open) => {
          setShowQuoteView(open);
          if (!open) setSelectedQuote(null);
        }}
        quoteId={selectedQuote?.id}
        onQuoteUpdated={() => {
          fetchCustomerProfile();
        }}
        onConvertedToJob={() => {
          setShowQuoteView(false);
          setSelectedQuote(null);
          fetchCustomerProfile();
        }}
        existingQuote={selectedQuote}
      />
    </div>
  );
}
